import { useState, useRef, useEffect } from 'react';

export const useSerialConnection = (onDataReceived) => {
    const [connected, setConnected] = useState(false);
    const [status, setStatus] = useState('Disconnected');
    const [portName, setPortName] = useState('');
    const [isReconnecting, setIsReconnecting] = useState(false);

    const portRef = useRef(null);
    const readerRef = useRef(null);
    const writerRef = useRef(null);
    const keepReadingRef = useRef(false);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    const readSerialData = async () => {
        const port = portRef.current;
        if (!port) return;
        
        keepReadingRef.current = true;
        
        try {
            const decoder = new TextDecoderStream();
            port.readable.pipeTo(decoder.writable);
            const reader = decoder.readable.getReader();
            readerRef.current = reader;
            
            let buffer = '';
            
            while (keepReadingRef.current) {
                const { value, done } = await reader.read();
                if (done) break;
                
                buffer += value;
                
                const lines = buffer.split('\n');
                buffer = lines.pop();
                
                for (const line of lines) {
                    const trimmed = line.trim();
                    if (trimmed && onDataReceived) {
                        onDataReceived(trimmed);
                    }
                }
            }
        } catch (error) {
            console.error('Read error:', error);
            handleDisconnect();
            startReconnection();
        }
    };

    const handleDisconnect = () => {
        setConnected(false);
        setStatus('Disconnected');
        reconnectAttemptsRef.current = 0;
        setIsReconnecting(false);
        
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    };

    const startReconnection = () => {
        if (isReconnecting) return;

        setIsReconnecting(true);
        setStatus('Reconnecting...');

        const attemptReconnection = async () => {
            try {
                reconnectAttemptsRef.current++;

                // Properly clean up existing connections
                keepReadingRef.current = false;

                if (readerRef.current) {
                    try {
                        await readerRef.current.cancel();
                    } catch (e) {}
                    readerRef.current = null;
                }

                if (writerRef.current) {
                    try {
                        await writerRef.current.releaseLock();
                    } catch (e) {}
                    writerRef.current = null;
                }

                if (portRef.current) {
                    try {
                        await portRef.current.close();
                    } catch (e) {}
                    portRef.current = null;
                }

                // Small delay to ensure cleanup is complete
                await new Promise(resolve => setTimeout(resolve, 300));

                // Use getPorts() to reconnect to already-permitted ports
                const ports = await navigator.serial.getPorts();
                if (ports.length === 0) {
                    throw new Error('No permitted ports available');
                }

                const port = ports[0];
                await port.open({ baudRate: 115200 });

                portRef.current = port;
                writerRef.current = port.writable.getWriter();

                setPortName('Device');
                setConnected(true);
                setStatus('Connected');
                setIsReconnecting(false);
                reconnectAttemptsRef.current = 0;

                readSerialData();

            } catch (error) {
                console.error(`Reconnection attempt ${reconnectAttemptsRef.current} failed:`, error);

                if (reconnectAttemptsRef.current < 10) {
                    setStatus(`Reconnecting... (${reconnectAttemptsRef.current}/10)`);
                    reconnectTimeoutRef.current = setTimeout(attemptReconnection, 500);
                } else {
                    setStatus('Device lost. Click Connect to reconnect.');
                    setIsReconnecting(false);
                    reconnectAttemptsRef.current = 0;
                }
            }
        };

        reconnectTimeoutRef.current = setTimeout(attemptReconnection, 500);
    };

    const connect = async () => {
        try {
            const port = await navigator.serial.requestPort();
            await port.open({ baudRate: 115200 });
            
            portRef.current = port;
            writerRef.current = port.writable.getWriter();
            
            setPortName('Device');
            setConnected(true);
            setStatus('Connected');
            
            readSerialData();
        } catch (error) {
            console.error('Connection error:', error);
            setStatus('Error: ' + error.message);
            handleDisconnect();
        }
    };

    const disconnect = async () => {
        keepReadingRef.current = false;
        setIsReconnecting(false);
        
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        
        if (readerRef.current) {
            try {
                await readerRef.current.cancel();
            } catch (e) {}
            readerRef.current = null;
        }
        
        if (writerRef.current) {
            try {
                await writerRef.current.releaseLock();
            } catch (e) {}
            writerRef.current = null;
        }
        
        if (portRef.current) {
            try {
                await portRef.current.close();
            } catch (e) {}
            portRef.current = null;
        }
        
        setConnected(false);
        setStatus('Disconnected');
    };

    const sendCommand = async (key, value) => {
        if (!writerRef.current) return;
        
        try {
            const cmd = `<${key}:${value}\n`;
            await writerRef.current.write(new TextEncoder().encode(cmd));
        } catch (error) {
            console.error('Write error:', error);
            handleDisconnect();
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            keepReadingRef.current = false;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (readerRef.current) {
                readerRef.current.cancel().catch(() => {});
            }
            if (writerRef.current) {
                writerRef.current.releaseLock().catch(() => {});
            }
            if (portRef.current) {
                portRef.current.close().catch(() => {});
            }
        };
    }, []);

    return {
        connected,
        status,
        portName,
        isReconnecting,
        connect,
        disconnect,
        sendCommand
    };
};